import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ArrowRight, Users, Star } from "lucide-react";
import { getDiscover, getCreatorId } from "../lib/api";

export default function Discover() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { setData(await getDiscover(getCreatorId())); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-32 gap-3">
        <Loader2 className="h-6 w-6 animate-spin text-[#FF3B30]" />
        <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Calculating taste overlaps...</p>
      </div>
    );
  }

  if (!data || data.empty) {
    return (
      <div className="mx-auto max-w-[1200px] px-6 py-20 md:px-10">
        <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-[#FF3B30]">// Vol. 08</p>
        <h1 className="mt-2 font-display text-6xl uppercase text-white md:text-8xl">Discover</h1>
        <p className="mt-4 max-w-lg text-neutral-400">
          Add some anime to your catalogue first, then come back — we&apos;ll find other viewers whose taste overlaps yours.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1400px] px-6 py-10 md:px-10">
      <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-[#FF3B30]">// Vol. 08</p>
      <h1 className="mt-2 font-display text-6xl uppercase leading-[0.9] tracking-tight text-white md:text-8xl">
        Discover
      </h1>
      <p className="mt-4 max-w-lg text-sm text-neutral-400">
        Other viewers ranked by how much their genre profile overlaps with yours. Cosine similarity across every genre in the catalogue.
      </p>

      {/* Me strip */}
      <div className="mt-8 flex flex-wrap items-center gap-3 border-y border-white/10 py-4">
        <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Comparing against you:</span>
        <span className="border border-[#FF3B30] bg-[#FF3B30]/10 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-white">
          {data.you.personality_type}
        </span>
        <span className="border border-white/15 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-white">
          Top: {data.you.top_genre}
        </span>
        <span className="ml-auto flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
          <Users className="h-3 w-3" /> {data.pool_size} public snapshot{data.pool_size === 1 ? "" : "s"} in pool
        </span>
      </div>

      {data.matches.length === 0 ? (
        <div data-testid="discover-empty" className="mt-16 border border-dashed border-white/15 py-20 text-center">
          <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-neutral-600">// Empty pool</p>
          <h2 className="mt-4 font-display text-4xl uppercase text-white">No other viewers yet</h2>
          <p className="mx-auto mt-3 max-w-md text-sm text-neutral-400">
            Ask a friend to run their catalogue through this app and hit &quot;Share Link&quot; — their snapshot will show up here.
          </p>
        </div>
      ) : (
        <div data-testid="discover-grid" className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {data.matches.map((m, i) => (
            <Link
              key={m.share_id}
              to={m.url_path}
              data-testid={`discover-card-${m.share_id}`}
              className="group flex flex-col border border-white/15 bg-neutral-950 brutal-hover"
              style={{ animation: `fadeUp 0.5s ${i * 0.05}s both ease-out` }}
            >
              {/* Overlap ribbon */}
              <div className="flex items-baseline justify-between border-b border-white/10 bg-black/60 px-5 py-3">
                <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Taste Overlap</span>
                <span className="font-display text-4xl leading-none text-[#FF3B30]">{m.overlap_percent}%</span>
              </div>
              <div className="p-5">
                <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Persona</p>
                <h3 className="mt-1 font-display text-3xl uppercase leading-tight text-white group-hover:text-[#FF3B30] md:text-4xl">
                  {m.personality_type}
                </h3>
                <div className="mt-3 flex flex-wrap items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-neutral-400">
                  <span className="border border-[#FF3B30]/40 px-2 py-1 text-[#FF3B30]">Top: {m.top_genre}</span>
                  <span className="border border-white/15 px-2 py-1 text-white">{m.total_anime} anime</span>
                </div>

                {m.shared_genres?.length > 0 && (
                  <div className="mt-4">
                    <p className="font-mono text-[9px] uppercase tracking-widest text-neutral-500">Shared genres</p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {m.shared_genres.map((g) => (
                        <span key={g} className="border border-white/15 px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-white">
                          {g}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {m.signature_shows?.length > 0 && (
                  <div className="mt-5 flex gap-2 overflow-hidden">
                    {m.signature_shows.map((s) => (
                      <div key={s.mal_id} className="flex-1 min-w-0">
                        <div className="relative aspect-[2/3] overflow-hidden border border-white/10 bg-neutral-900">
                          {s.image_url && <img src={s.image_url} alt="" className="h-full w-full object-cover" loading="lazy" />}
                          {s.user_rating != null && (
                            <span className="absolute right-1 top-1 flex items-center gap-0.5 border border-white/20 bg-black/70 px-1 font-mono text-[8px] text-white">
                              <Star className="h-2 w-2 fill-[#FF3B30] text-[#FF3B30]" />
                              {Number(s.user_rating).toFixed(1)}
                            </span>
                          )}
                        </div>
                        <p className="mt-1 truncate font-mono text-[9px] text-neutral-400">{s.title}</p>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-5 flex items-center justify-end gap-1 font-mono text-[10px] uppercase tracking-widest text-neutral-500 group-hover:text-white">
                  View persona <ArrowRight className="h-3 w-3" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
